import {useCallback, useMemo} from 'react';
import {useFormikContext, getIn} from 'formik';
import {useTranslation} from 'react-i18next';
import {BUTTON_THEME, BUTTON_VARIANT} from '@/constants/button';
import {AllocationAccount} from '@/repositories/beneva-api/endpoints/allocations';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import Button from '../button';
import Combobox from '../form/combobox';
import FieldWrapper from '../form/field-wrapper';
import FormControl from '../form/form-control';
import PercentageInput from '../form/percentage-input';
import {AllocationFormSchemaErrors, AllocationFormSchemaType} from './allocation-form-schema';

type InvestmentAccountSubformProps = {
  investmentAccounts: {value: string; label: string}[];
  accountTypeName: string;
  account: AllocationAccount;
  index: number;
  onRemove: () => void;
};

export const InvestmentAccountSubform = ({
  investmentAccounts,
  accountTypeName,
  account,
  index,
  onRemove,
}: InvestmentAccountSubformProps) => {
  const {handleChange, handleBlur, errors, touched, setFieldValue} = useFormikContext<AllocationFormSchemaType>();
  const {t} = useTranslation();

  const tError = useCallback(
    (key?: AllocationFormSchemaErrors) => (key ? t(`components.advisor-predetermined-allocation-form.${key}`) : ''),
    [t],
  );

  const selectedValue = useMemo(() => {
    const foundItem = investmentAccounts?.find(a => a.value === account.accountId);
    return foundItem ? {id: foundItem.value, value: foundItem.value, label: foundItem.label} : null;
  }, [investmentAccounts, account.accountId]);

  const values = useMemo(() => {
    return (investmentAccounts ?? []).map(({value, label}) => ({id: value, value, label}));
  }, [investmentAccounts]);

  const accountValues = useMemo(() => {
    const id = `accounts[${index}].accountId`;
    return {
      id,
      dataTestId: `${id}-select`,
      isInError: !!getIn(errors, id) && getIn(touched, id),
      errors: getIn(touched, id) ? [tError(getIn(errors, id))] : [],
    };
  }, [errors, touched, index, tError]);

  const amountValues = useMemo(() => {
    const id = `accounts[${index}].amount`;
    return {
      id,
      dataTestId: `${id}-input`,
      isInError: !!getIn(errors, id) && getIn(touched, id),
      errors: getIn(touched, id) ? [tError(getIn(errors, id))] : [],
    };
  }, [errors, touched, index, tError]);

  return (
    <div key={index}>
      <div className="flex items-start gap-4">
        <div className="w-3/5 flex-shrink-0">
          <FormControl isRequired={true} isInError={accountValues.isInError} errors={accountValues.errors}>
            <FieldWrapper label={accountTypeName} identifier={accountValues.id} className="w-2/3">
              <Combobox
                placeholder={t('components.advisor-predetermined-allocation-form.table.select-placeholder')}
                data-testid={accountValues.dataTestId}
                onChange={e => handleChange(accountValues.id)(e?.value ?? '')}
                selectedValue={selectedValue}
                values={values}
              ></Combobox>
            </FieldWrapper>
          </FormControl>
        </div>

        <FormControl isRequired={true} isInError={amountValues.isInError} errors={amountValues.errors}>
          <FieldWrapper
            label={t('components.advisor-predetermined-allocation-form.label-amount')}
            identifier={amountValues.id}
          >
            <PercentageInput
              type="number"
              data-testid={amountValues.dataTestId}
              name={amountValues.id}
              value={account.amount}
              onChange={e => setFieldValue(amountValues.id, parseInt(e.target.value, 10))}
              onBlur={handleBlur(amountValues.id)}
              max={100}
              min={0}
              maxLength={3}
            />
          </FieldWrapper>
        </FormControl>

        <div className="flex flex-col items-center justify-center self-stretch">
          <Button
            type="button"
            theme={BUTTON_THEME.SECONDARY}
            variant={BUTTON_VARIANT.SMALL}
            className="self-end"
            label={t('components.advisor-predetermined-allocation-form.table.remove')}
            onClick={onRemove}
            trackingKey={WEB_CLICK_TRACKING_KEY.REMOVE_ALLOCATION_ACCOUNT}
          />
        </div>
      </div>
    </div>
  );
};
